// Sentry
import { buildRoidFallback, formatDirectionCellLabel, mapRouteStateToTagVariant } from '../../utils/surveillanceRoute';
import * as Sentry from '@sentry/react';
import debug from 'debug';

// GQL
import { client } from '../client';

const log = debug('rois:gql:selectors:orders');

if (import.meta.env.DEV) {
  debug.enable('rois:gql:selectors:orders*');
}
import i18n from '../../i18n';
import {
  GET_CONCESSIONS,
  GET_ORDER_BY_ID,
  GET_ORDER_LINKED_ROUTES,
  GET_ORDER_UNLINKED_WORK_ROUTES,
} from '../queries/orders/orders';
import {
  LinkedRouteRef,
  UnlinkedWorkRouteRef,
} from '../../types/surveillanceRoute';
import {
  routesForWork,
  unlinkedRoutesForWork,
} from '../../utils/workedInRoutes';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
} from '../queries/orders/predefinedData';
import { GET_CAMPAIGN_BY_ID } from '../queries/campaign';
import { GET_GENERIC_ORDER_BY_ID } from '../queries/genericOrders';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
  GET_ROADS,
} from '../queries/geoPositions/roads';
import {
  FETCH_TEAMS,
  FETCH_USERS,
  GET_TEAM_BY_ID,
  GET_USER_BY_ID,
} from '../queries/users';
import {
  GET_COMMUNICATION_BY_ID,
  GET_INTERLOCUTOR_GROUP_BY_ID,
} from '../queries/communication';
import { GET_ACCIDENT_BY_ID } from '../queries/accidents/accidents';

//Types
import { OrderType } from '../../types/orders';
import { OperatorType } from '../../components/display/ShowOT/types';

// Utils
import { laneLabel, trackLabel } from '../../utils/catalogLabels';
import { ensureLabelNamespaces } from '../../utils/i18nReady';
import { pkFormatter } from '../../utils/transform';
import { GET_ACTION_BY_ID } from '../queries/actions/actions';
import {
  GET_ROUTE_SEGMENT,
  GET_ROUTE_SEGMENTS,
  GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
} from '../queries/surveillanceRoute';
import { warmupOrdersByIds } from './warmupOrders';
import { warmupActionsByIds } from './warmupActions';
import { warmupVehicleOccupantsByIds } from './warmupVehicleOccupants';
import { warmupCommunicationsByIds } from './warmupCommunications';
import { warmupAccidentsByIds } from './warmupAccidents';
import { isLiveLinkedBasicAction } from '../../utils/segmentLinkedBasicAction';

/**
 * Cross-link 3 (2026-09-22): recorridos vinculados a una OT genérica/campaña,
 * cronológicos y con procedencia. Solo lo pide el detalle (esta función vive
 * en el selector del árbol, que ya es por-id). Fail-open: sin dato → `[]`.
 */
async function resolveLinkedRoutes(orderId: string): Promise<LinkedRouteRef[]> {
  try {
    const { data } = await client.query({
      query: GET_ORDER_LINKED_ROUTES,
      variables: { id: orderId },
      fetchPolicy: 'network-only',
    });
    return data?.order?.linkedSurveillanceRoutes ?? [];
  } catch {
    return [];
  }
}

/**
 * Spec § 16: trabajos de la OT hechos por una patrulla sin vincular. Misma
 * forma que `resolveLinkedRoutes` y fail-open igual: sin dato → `[]`.
 */
async function resolveUnlinkedWorkRoutes(
  orderId: string
): Promise<UnlinkedWorkRouteRef[]> {
  try {
    const { data } = await client.query({
      query: GET_ORDER_UNLINKED_WORK_ROUTES,
      variables: { id: orderId },
      fetchPolicy: 'network-only',
    });
    return data?.order?.unlinkedWorkRoutes ?? [];
  } catch {
    return [];
  }
}

/**
 * P15 Fase 2 (2026-07-09) — resuelve el RV vinculado a una OT externa
 * (GenericOrder/Campaign) via el tram intermedio. Cross-link
 * `Order(externa).externalLinkedSegmentOrderIDs` (self-ref Order↔Order).
 *
 * Resolución en 2 saltos, null-safe (sin vínculo o fallo intermedio → ambos
 * `undefined`, nunca throw — igual que el resto de resoluciones opcionales
 * de esta función):
 *   1. `Order(externa).externalLinkedSegmentOrderIDs[0]` = Order del tram →
 *      fetch esa Order → `parentExpeditionOrderIDs[0]` = Order del RV.
 *   2. `surveillanceRouteByOrderId(rvOrderId)` → `{ id, indexExpedition, indexType }`
 *      → roid `Su.NN` (mismo helper que `adaptSurveillanceRouteToOrder`) +
 *      `routeId` (`sr.id`, entity id — target de navegación
 *      `/surveillance-route/:routeId`, NO el id de la Order).
 *
 * `fetchPolicy` por defecto (cache-first) a propósito — a diferencia del
 * resto de fetches de esta función (network-only): esta resolución solo se
 * dispara en el detalle (no en listados) y se beneficia de reusar cache si
 * el usuario ya visitó el detalle del RV. Ver
 * docs/superpowers/specs/2026-07-09-p15-fase2-external-link-design.md.
 */
const resolveLinkedRv = async (
  order: { externalLinkedSegmentOrderIDs?: string[] },
): Promise<{
  linkedRvRoid?: string;
  linkedRvRouteId?: string;
  linkedSegmentEntityId?: string;
}> => {
  const tramOrderId = order.externalLinkedSegmentOrderIDs?.[0];
  if (!tramOrderId) return {};
  try {
    const {
      data: { order: tramOrder },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: tramOrderId },
    });
    const rvOrderId = tramOrder?.parentExpeditionOrderIDs?.[0];
    if (!rvOrderId) return {};

    const {
      data: { surveillanceRouteByOrderId: sr },
    } = await client.query({
      // Perf 2026-07-21: variante ligera (sin gpsPoints/segments, 61,4kB→~1kB)
      // — esta resolución solo lee id + indexExpedition/indexType.
      query: GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
      variables: { orderId: rvOrderId },
    });
    if (!sr) return {};

    return {
      linkedRvRoid: buildRoidFallback(sr) || undefined,
      linkedRvRouteId: sr.id,
      // Entity RouteSegment del tramo vinculado (trampa entidad doble: las
      // mutations unlink/link quieren el id de la ENTITY, no el de la Order).
      linkedSegmentEntityId: tramOrder?.routeSegmentIDs?.[0],
    };
  } catch (err) {
    console.log('Error resolving linked RV for external order:', err);
    return {};
  }
};

export const selectOrderByIdForFillFormData = async (
  orderId: string,
  isCampaign = false
) => {
  try {
    let dataFromOrderDependsOnType;
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderId },
    });

    if (isCampaign) {
      const {
        data: { campaign },
      } = await client.query({
        query: GET_CAMPAIGN_BY_ID,
        variables: {
          id: order.campaignIDs[0],
        },
      });
      dataFromOrderDependsOnType = campaign;
    } else {
      const {
        data: { genericOrder },
      } = await client.query({
        query: GET_GENERIC_ORDER_BY_ID,
        variables: {
          id: order.genericOrdersIDs[0],
        },
      });
      dataFromOrderDependsOnType = genericOrder;
    }
    const [
      {
        data: { concession },
      },
      {
        data: { road },
      },
    ] = await Promise.all([
      client.query({
        query: GET_CONCESSION_BY_ID,
        variables: {
          id: dataFromOrderDependsOnType.concessionIDs[0],
        },
      }),
      client.query({
        query: GET_ROAD_BY_ID,
        variables: {
          id: dataFromOrderDependsOnType.roadIDs[0],
        },
      }),
    ]);

    return {
      id: order.id,
      roid:
        typeof order.roid === 'string'
          ? order.roid.split('.')
          : Array.isArray(order.roid)
            ? order.roid
            : [],
      registerInitDate: order.registerInitDate,
      pkInit: pkFormatter(
        dataFromOrderDependsOnType.pkInitKm,
        dataFromOrderDependsOnType.pkInitMeter
      ),
      pkEnd: pkFormatter(
        dataFromOrderDependsOnType.pkEndKm,
        dataFromOrderDependsOnType.pkEndMeter
      ),
      description: order.description,
      notes: order.notes,
      capitol: order.capitolIDs[0],
      subCapitol: order.subCapitolIDs[0],
      classification: order.classificationIDs[0],
      teamRelationships: dataFromOrderDependsOnType?.teamIDs ?? [],
      margin: dataFromOrderDependsOnType.margin,
      track: dataFromOrderDependsOnType.track,
      lane: dataFromOrderDependsOnType.lane,
      concession: concession.id,
      city: concession.location,
      pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter, true),
      pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter, true),
      road: dataFromOrderDependsOnType.roadIDs[0],
      direction: dataFromOrderDependsOnType.direction,
      campaign: isCampaign ? dataFromOrderDependsOnType.name : '',
      modelTypeId: isCampaign ? order.campaignIDs[0] : '',
      userId: order.userId,
      predefine: order.predefinedOrderIDs?.[0],
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('[órdenes] error obteniendo los datos', err);
  }
};

export const selectWorkOrderWithChildren = async (
  onlyOrder = true,
  idOrder: string
) => {
  try {
    let orders = [];

    const actions: {
      id: string;
      actionID?: string;
      roid: string;
      tag: string;
      type: string;
      date: string;
      endDate: string;
      creationDate?: string;
      operators: OperatorType[];
      laneCutting: string;
      capitols: string;
      capitol: string;
      subCapitol?: string;
      subCapitolsValues?: { id: string; name: string; elementOrdersIds: string }[];
      predefine?: string;
      predefinedOrders?: { id: string; name: string; capitolId?: string; subCapitolId?: string; elementOrderId?: string; activityId?: string }[];
      description: string;
      notes?: string;
      pkInit?: string;
      pkEnd?: string;
      registerInitDate?: string;
      isTransferred: boolean;
      state: string;
      userName?: string;
      registeredBy?: string;
      imageIDs?: string[];
      // Origen (básica del RV = `surveillanceRoute`): la card no pinta
      // «Operarios» en una básica (2026-09-04).
      containerType?: string | null;
      // P15 Fase 1 (2026-07-08) — tram vinculado (RV children únicamente).
      linkedSegmentRoid?: string;
      linkedSegmentOrderId?: string;
      linkedSegmentEntityId?: string;
    }[] = [];

    const accidents: {
      id: string;
      accidentID: string; // ID del Accident entity para navegación al detail
      roid: string;
      tag: string;
      type: string;
      date: string;
      creationDate: string;
      capitols: string;
      capitol: string;
      subCapitolsValues: string;
      subCapitol: string;
      predefine?: string;
      predefinedOrders?: { id: string; name: string; capitolId?: string; subCapitolId?: string; elementOrderId?: string; activityId?: string }[];
      description: string;
      notes?: string;
      pkInit?: string;
      pkEnd?: string;
      isTransferred: boolean;
      state: string;
      userName?: string;
      registeredBy?: string;
      imageIDs?: string[];
    }[] = [];

    const communications: {
      id: string;
      communicationID: string; // ID de la Communication entity para navegación al detail
      roid: string;
      tag: string;
      type: string;
      date: string;
      creationDate: string;
      capitols: string;
      capitol: string;
      subCapitolsValues: string;
      subCapitol: string;
      predefine?: string;
      predefinedOrders?: { id: string; name: string; capitolId?: string; subCapitolId?: string; elementOrderId?: string; activityId?: string }[];
      description: string;
      notes?: string;
      pkInit?: string;
      pkEnd?: string;
      groupInter?: string;
      sendStatus?: string;
      isTransferred: boolean;
      state: string;
      userName?: string;
    }[] = [];

    // V4 P8 FR-8.3 (2026-04-22): tramos (RouteSegment) del RV padre.
    // El `linkedOrderRoid` se resuelve desde `Order.segmentLinkedBasicActionOrderIDs`
    // o `segmentLinkedExternalOrderIDs` (self-ref Order↔Order PR 1.1).
    const routeSegments: {
      id: string;
      /** id de la ENTITY RouteSegment (D6) — ver comentario en el push. */
      segmentId?: string;
      roid: string;
      tag: string;
      type: string;
      date: string;
      endDate: string;
      startDate?: number;
      pkInit?: string;
      pkEnd?: string;
      direction?: string;
      lane?: string;
      description: string;
      pauseCause?: string | null;
      pauseCauseDetail?: string | null;
      pendingExternalLink?: boolean;
      pendingActionLink?: boolean;
      roadStretches?: {
        roadId: string;
        pkInitKm: number;
        pkInitMeter: number;
        pkEndKm: number;
        pkEndMeter: number;
      }[];
      /** Fotos de la intervención leve — las lee la card del tramo. */
      imageIDs?: string[];
      linkedOrderRoid?: string;
      // P15 Fase 1 (2026-07-08) — ids del Order vinculado, para navegación
      // (pills "Resolt per At.NN" / "Vinculat amb OT.NN"). `linkedActionId`
      // es el id de la ENTITY Action (no el de su Order) — target de
      // `/work-orders/actions-validation/summary/:id`. `linkedOrderId` es el
      // id crudo de la Order externa vinculada — target de `/work-orders/:id`
      // (o `/work-orders/campaign/:id` si `linkedOrderType==='campaign'`, fix
      // ronda 1).
      linkedOrderId?: string;
      linkedActionId?: string;
      linkedOrderType?: OrderType;
      isTransferred: boolean;
      /** Tramo transferido: de qué recorrido y tramo vino, y cuándo. */
      transferredFrom?: {
        fromRouteRoid?: string | null;
        fromRoid?: string | null;
        at: string;
      };
      state: string;
      userName?: string;
    }[] = [];

    const [
      {
        data: { order },
      },
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { roads },
      },
      {
        data: { subCapitols },
      },
      {
        data: { classifications },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { teams },
      },
    ] = await Promise.all([
      client.query({
        query: GET_ORDER_BY_ID,
        variables: { id: idOrder },
        fetchPolicy: 'network-only',
      }),
      client.query({
        query: FETCH_CAPITOLS,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: GET_ROADS,
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
      }),
      client.query({
        query: FETCH_TEAMS,
      }),
    ]);

    // Perf detalle RV (2026-07-21): id de la ENTITY SurveillanceRoute del
    // padre (branch SR de abajo) — lo necesita el warm-up plural
    // `GetRouteSegments(surveillanceRouteId)` previo al loop de hijas.
    let surveillanceRouteEntityId: string | undefined;

    if (onlyOrder) {
      // Fetch user name if order has userId
      let userName: string | undefined;
      if (order.userId) {
        try {
          const {
            data: { user },
          } = await client.query({
            query: GET_USER_BY_ID,
            variables: {
              input: {
                id: order.userId,
              },
            },
          });
          userName = user ? `${user.name} ${user.surName}`.trim() : undefined;
        } catch (err) {
          console.log('Error fetching user for order:', err);
        }
      }

      switch (order.type) {
        case OrderType.Generic: {
          const {
            data: { genericOrder },
          } = await client.query({
            query: GET_GENERIC_ORDER_BY_ID,
            variables: {
              id: order.genericOrdersIDs[0],
            },
            fetchPolicy: 'network-only',
          });

          const {
            data: { classification },
          } = await client.query({
            query: GET_CLASSIFICATION_BY_ID_OR_ROID,
            variables: {
              id: order.classificationIDs[0],
            },
          });

          // P15 Fase 2 (2026-07-09): RV vinculado (si n'hi ha) via
          // `externalLinkedSegmentOrderIDs`. Zero queries extra sin vínculo.
          const { linkedRvRoid, linkedRvRouteId, linkedSegmentEntityId } =
            await resolveLinkedRv(order);
          // Cross-link 3 (2026-09-22): recorridos que trabajaron esta OT.
          // Query propia del detalle (nunca en NewOrder); si falla, la card
          // sale sin bloque — no se bloquea el detalle por esto.
          const [linkedRoutes, unlinkedWorkRoutes] = await Promise.all([
            resolveLinkedRoutes(order.id),
            resolveUnlinkedWorkRoutes(order.id),
          ]);

          orders.push({
            id: order.id,
            roid: order.roid,
            // Fix 2026-07-04: los estados del RV (in-progress/paused) no son
            // variants de SpecialTag → la card padre salía SIN chip de estado.
            tag:
              order.type === 'surveillance-route'
                ? mapRouteStateToTagVariant(order.state)
                : order.state,
            type: order.type,
            capitols: capitols,
            concessions: concessions,
            roadsValues: roads,
            road: genericOrder.roadIDs[0],
            capitol: order.capitolIDs[0],
            subCapitol: order.subCapitolIDs[0],
            subCapitolsValues: subCapitols,
            concession: genericOrder.concessionIDs[0],
            pkInit: pkFormatter(
              genericOrder.pkInitKm,
              genericOrder.pkInitMeter
            ),
            lane: genericOrder.lane,
            direction: genericOrder.direction,
            margin: genericOrder.margin,
            track: genericOrder.track,
            pkEnd: pkFormatter(genericOrder.pkEndKm, genericOrder.pkEndMeter),
            date: order.registerInitDate,
            derivedEndDate: order.derivedEndDate ?? null,
            description: order.description,
            notes: order.notes,
            modelTypeId: order.genericOrdersIDs[0],
            indexExpedition: order.indexExpedition,
            state: order.state,
            closedAt: order.closedAt ?? null,
            classification: classification.name,
            classificationId: classification.id,
            classifications: classifications,
            userName,
            predefine: order.predefinedOrderIDs?.[0],
            predefinedOrders,
            teams,
            teamRelationships: genericOrder.teamIDs ?? [],
            // P15 Fase 2 — pill "Vinculat amb Su.NN" / "Vincular a recorregut".
            linkedRvRoid,
            linkedRvRouteId,
            linkedSegmentEntityId,
            linkedRoutes,
            unlinkedWorkRoutes,
          });
          break;
        }
        case OrderType.Campaign: {
          const {
            data: { campaign },
          } = await client.query({
            query: GET_CAMPAIGN_BY_ID,
            variables: {
              id: order.campaignIDs[0],
            },
            fetchPolicy: 'network-only',
          });

          const {
            data: { classification },
          } = await client.query({
            query: GET_CLASSIFICATION_BY_ID_OR_ROID,
            variables: {
              id: order.classificationIDs[0],
            },
          });

          // P15 Fase 2 (2026-07-09): RV vinculado (si n'hi ha) via
          // `externalLinkedSegmentOrderIDs`. Zero queries extra sin vínculo.
          const { linkedRvRoid, linkedRvRouteId, linkedSegmentEntityId } =
            await resolveLinkedRv(order);
          // Cross-link 3 (2026-09-22): recorridos que trabajaron esta OT.
          // Query propia del detalle (nunca en NewOrder); si falla, la card
          // sale sin bloque — no se bloquea el detalle por esto.
          const [linkedRoutes, unlinkedWorkRoutes] = await Promise.all([
            resolveLinkedRoutes(order.id),
            resolveUnlinkedWorkRoutes(order.id),
          ]);

          orders.push({
            id: order.id,
            name: campaign.name,
            roid: order.roid,
            tag: order.state,
            type: order.type,
            capitols: capitols,
            concessions: concessions,
            roadsValues: roads,
            road: campaign.roadIDs[0],
            capitol: order.capitolIDs[0],
            subCapitol: order.subCapitolIDs[0],
            subCapitolsValues: subCapitols,
            concession: campaign.concessionIDs[0],
            pkInit: pkFormatter(campaign.pkInitKm, campaign.pkInitMeter),
            pkEnd: pkFormatter(campaign.pkEndKm, campaign.pkEndMeter),
            lane: campaign.lane,
            direction: campaign.direction,
            margin: campaign.margin,
            track: campaign.track,
            date: order.registerInitDate,
            derivedEndDate: order.derivedEndDate ?? null,
            description: order.description,
            notes: order.notes,
            modelTypeId: order.campaignIDs[0],
            indexExpedition: order.indexExpedition,
            state: order.state,
            closedAt: order.closedAt ?? null,
            classification: classification.name,
            classificationId: classification.id,
            classifications: classifications,
            userName,
            predefine: order.predefinedOrderIDs?.[0],
            predefinedOrders,
            teams,
            teamRelationships: campaign.teamIDs ?? [],
            // P15 Fase 2 — pill "Vinculat amb Su.NN" / "Vincular a recorregut".
            linkedRvRoid,
            linkedRvRouteId,
            linkedSegmentEntityId,
            linkedRoutes,
            unlinkedWorkRoutes,
          });
          break;
        }
        case OrderType.SurveillanceRoute: {
          // SR orders don't have genericOrdersIDs — the SR entity is fetched
          // via surveillanceRouteByOrderId. La fila conserva su type REAL
          // (surveillanceRoute) — ShowOT lo renderiza nativo desde 2026-07-21
          // (antes se reescribía a `generic` como shim de render).
          const {
            data: { surveillanceRouteByOrderId: srData },
          } = await client.query({
            // Perf 2026-07-21: variante ligera (sin gpsPoints/segments,
            // 61,4kB/1,9s→~1kB medidos) — esta card solo lee escalares +
            // ids de Ubicació. Ver SURVEILLANCE_ROUTE_CARD_FRAGMENT.
            query: GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
            variables: { orderId: order.id },
            fetchPolicy: 'network-only',
          });

          if (srData) {
            surveillanceRouteEntityId = srData.id;
            orders.push({
              id: order.id,
              roid: order.roid,
              // Fix 2026-07-04 (2º intento — este es el branch real de la card
              // padre del RV): mapear in-progress/paused a variants del chip.
              tag: mapRouteStateToTagVariant(order.state),
              type: OrderType.SurveillanceRoute,
              capitols: capitols,
              concessions: concessions,
              roadsValues: roads,
              road: srData.roadIDs?.[0],
              // Multi-carretera: la tarjeta necesita TODAS para poder pintar una
              // fila por vía. `road` (singular) se conserva porque lo consumen
              // el resto de tipos de OT, que van por una sola.
              routeRoadIDs: srData.roadIDs,
              routeRoadRanges: srData.roadRanges,
              capitol: order.capitolIDs?.[0],
              subCapitol: order.subCapitolIDs?.[0],
              subCapitolsValues: subCapitols,
              concession: srData.concessionIDs?.[0],
              pkInit: pkFormatter(srData.pkInitKm, srData.pkInitMeter),
              pkEnd: pkFormatter(srData.pkEndKm, srData.pkEndMeter),
              lane: srData.lane || '',
              direction: srData.direction || '',
              margin: srData.margin || '',
              track: srData.track || '',
              date: order.registerInitDate,
              description: order.description,
              notes: order.notes,
              // D3 (2026-07-20): "Nombre del recorrido" read-only en la card
              // gemela (`ShowOT/WorkOrder`, modo `isSurveillanceRouteOwnOrder`).
              // Antes no se mapeaba — el `name` que llega a `WorkOrder` se
              // sobreescribe siempre con el label traducido genérico
              // ("Orden de trabajo") en `ShowOT/index.tsx`, así que necesita
              // un campo propio.
              routeName: srData.name,
              modelTypeId: srData.id,
              indexExpedition: order.indexExpedition,
              state: order.state,
              classification: '',
              classificationId: '',
              classifications: classifications,
              // 2026-07-17: marca la card de la PROPIA gemela del RV para que
              // WorkOrder recorte el formulario a los campos del dominio RV
              // (sin predefinidos/capítulo/clasificación/tipo vial/sentido/
              // margen/carril) y el action de child-orders enrute el guardado
              // a updateSurveillanceRoute en vez de SaveGenericOrder.
              isSurveillanceRouteOwnOrder: true,
              userName,
              predefine: order.predefinedOrderIDs?.[0],
              predefinedOrders,
              teams,
              // Fix 2026-07-17 (Task F2): antes publicaba `srData.teamIDs?.[0] || ''`
              // (string) — el comparador de WorkOrder/index.tsx:516-529 castea
              // `teamRelationships` a `string[]` y hace `[...currentTeams]`, que
              // sobre un string spreadea CARACTERES sueltos en vez de team ids,
              // rompiendo el diff "¿cambió el equipo?" para la card de la propia
              // gemela del RV. Las otras 3 ramas (order/genericOrder/campaign,
              // líneas 195/487/556) ya usan `?? []` — este era el único hueco.
              teamRelationships: srData.teamIDs ?? [],
            });
          }
          break;
        }
      }
    }

    // Warm-up fase 1 (receta #92, adaptada al detalle): UNA query `ordersByIds`
    // baja las Orders de TODAS las hijas — los GET_ORDER_BY_ID per-row de abajo
    // pasan a cache-first y dan hit vía el redirect `Query.order` de policies.ts,
    // en vez de N requests fila a fila (el N+1 GetOrderById×30 medido en prod).
    // `network-only` a propósito: este selector no tiene flag reFetch ni polling
    // — la frescura de CADA carga del detalle la garantiza el propio batch
    // (con cache-first, repetir visita con el mismo set de ids no iría a red).
    // Si el warm-up falla (offline, backend sin `ordersByIds`), los per-row
    // cache-first caen a red solos = comportamiento de hoy.
    const childIds: string[] = order?.childExpeditionOrderIDs ?? [];
    await warmupOrdersByIds(childIds, false, 'network-only');

    // Warm-up fase 2: cross-links de las hijas (Order del tram vinculado a una
    // actuación, Order/Action vinculada a un tram) + Actions de las hijas
    // (batch `actionsByIds`, Tier B) + tramos del RV (UNA `GetRouteSegments`
    // plural en vez de S× GetRouteSegment). Los ids salen de la caché recién
    // calentada (readQuery, cero red); si la fase 1 falló, readQuery devuelve
    // null y esta fase se salta sola — per-row como fallback.
    try {
      const linkedIds: (string | undefined)[] = [];
      const childActionIds: (string | undefined)[] = [];
      const childCommunicationIds: (string | undefined)[] = [];
      const childAccidentIds: (string | undefined)[] = [];
      let hasSegmentChildren = false;
      childIds.forEach((childId: string) => {
        const cached: {
          order?: {
            actionIDs?: string[];
            communicationIDs?: string[];
            accidentIDs?: string[];
            routeSegmentIDs?: string[];
            basicActionLinkedSegmentOrderIDs?: string[];
            segmentLinkedBasicActionOrderIDs?: string[];
            segmentLinkedExternalOrderIDs?: string[];
          } | null;
        } | null = client.readQuery({
          query: GET_ORDER_BY_ID,
          variables: { id: childId },
        });
        const child = cached?.order;
        linkedIds.push(
          child?.basicActionLinkedSegmentOrderIDs?.[0],
          child?.segmentLinkedBasicActionOrderIDs?.[0],
          child?.segmentLinkedExternalOrderIDs?.[0]
        );
        childActionIds.push(child?.actionIDs?.[0]);
        childCommunicationIds.push(child?.communicationIDs?.[0]);
        childAccidentIds.push(child?.accidentIDs?.[0]);
        if (child?.routeSegmentIDs?.[0]) hasSegmentChildren = true;
      });

      await Promise.all([
        warmupOrdersByIds(linkedIds, false, 'network-only'),
        // Tier B: las Actions de TODAS las hijas en 1 query (fragment
        // NewAction = el de GET_ACTION_BY_ID → hit garantizado del per-row
        // cache-first vía redirect Query.action). network-only por la misma
        // razón que la fase 1: la frescura de cada carga la da el batch.
        warmupActionsByIds(childActionIds, false, 'network-only'),
        // Tier C: las Communications de TODAS las hijas en 1 query (fragment
        // NewCommunication = el de GET_COMMUNICATION_BY_ID; Communication no
        // tiene ResolveFields → paridad completa, hit garantizado del per-row
        // cache-first vía redirect Query.communication). network-only por la
        // misma razón que la fase 1: la frescura de cada carga la da el batch.
        warmupCommunicationsByIds(childCommunicationIds, false, 'network-only'),
        // Tier D: los Accidents de TODAS las hijas en 1 query (fragment
        // NewAccident = el de GET_ACCIDENT_BY_ID; Accident no tiene
        // ResolveFields → paridad completa, hit garantizado del per-row
        // cache-first vía redirect Query.accident). El consumidor real es la
        // cadena `selectAccidentById` que `prefetchChildrenSummaries` del
        // loader lanza por cada hija accidente (~20 queries/accidente, la
        // primera era el getAccidentById per-hija). network-only por la
        // misma razón que la fase 1: la frescura de cada carga la da el batch.
        warmupAccidentsByIds(childAccidentIds, false, 'network-only'),
        // Detalle RV: los S tramos hijos en 1 query plural (MISMO
        // RouteSegmentFragment que GET_ROUTE_SEGMENT → paridad byte-exacta;
        // per-row cache-first + redirect Query.routeSegment de policies.ts).
        // Nunca gate: si falla, los per-row caen a red solos.
        hasSegmentChildren && surveillanceRouteEntityId
          ? client
              .query({
                query: GET_ROUTE_SEGMENTS,
                variables: { surveillanceRouteId: surveillanceRouteEntityId },
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
              })
              .catch(() => undefined)
          : Promise.resolve(undefined),
      ]);

      // Tier B (2ª ronda, depende de las Actions recién calentadas): los
      // VehicleOccupant de TODAS las actions hijas en 1 query — los per-row
      // GET_VEHICLE_OCCUPANT_BY_ID de `selectActionById` (fase 2 del loader,
      // preferCache) pasan a cache-first y dan hit vía el redirect
      // Query.vehicleOccupant. En el detalle RV se salta (RouteChildOrders
      // no prefetchea resúmenes de hijas → nadie leería los ocupantes).
      if (order?.type !== OrderType.SurveillanceRoute) {
        const occupantIds = childActionIds.flatMap((actionId) => {
          if (!actionId) return [];
          const cachedAction: {
            action?: { vehicleOccupantIDs?: string[] } | null;
          } | null = client.readQuery({
            query: GET_ACTION_BY_ID,
            variables: { id: actionId },
          });
          return cachedAction?.action?.vehicleOccupantIDs ?? [];
        });
        await warmupVehicleOccupantsByIds(occupantIds, false, 'network-only');
      }
    } catch {
      // Warm-up opcional: sin efectos si falla.
    }

    await Promise.all(
      order?.childExpeditionOrderIDs.map(async (orderId: string) => {
        try {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id: orderId },
          // Frescura garantizada por el warm-up network-only de arriba; si el
          // warm-up falló, cache-first va a red igual (miss) = hoy.
          fetchPolicy: 'cache-first',
        });
        if (order) {
          switch (order.type) {
            case OrderType.Action: {
              if (order?.actionIDs?.[0]) {
                const {
                  data: { action },
                } = await client.query({
                  query: GET_ACTION_BY_ID,
                  variables: { id: order.actionIDs[0] },
                  // Frescura garantizada por el warm-up `actionsByIds`
                  // network-only de arriba (mismo fragment NewAction, hit vía
                  // redirect Query.action); si el warm-up falló, cache-first
                  // hace miss y va a red = comportamiento de hoy.
                  fetchPolicy: 'cache-first',
                });
                const users = await Promise.all(
                  action.userIDs.map(async (userId: string) => {
                    try {
                      return await client.query({
                        query: GET_USER_BY_ID,
                        variables: {
                          input: {
                            id: userId,
                          },
                        },
                      });
                    } catch {
                      return { data: { user: null } };
                    }
                  })
                );

                // T34: Fetch userName for the order creator
                let childUserName: string | undefined;
                if (order.userId) {
                  try {
                    const {
                      data: { user },
                    } = await client.query({
                      query: GET_USER_BY_ID,
                      variables: {
                        input: {
                          id: order.userId,
                        },
                      },
                    });
                    childUserName = user ? `${user.name} ${user.surName}`.trim() : undefined;
                  } catch (err) {
                    console.log('Error fetching user for action order:', err);
                  }
                }

                // Operario que registra (Order.registeredByUserId → "Nombre
                // Apellido"), mismo patrón que el creador de arriba. Si es null
                // queda '' y la fila del detalle no pinta la línea.
                let childRegisteredBy = '';
                if (order.registeredByUserId) {
                  try {
                    const {
                      data: { user },
                    } = await client.query({
                      query: GET_USER_BY_ID,
                      variables: {
                        input: {
                          id: order.registeredByUserId,
                        },
                      },
                    });
                    childRegisteredBy = user ? `${user.name} ${user.surName}`.trim() : '';
                  } catch (err) {
                    console.log('Error fetching registeredBy user for action order:', err);
                  }
                }

                // P15 Fase 1 (2026-07-08): resolver el tram vinculado (si n'hi
                // ha) — recíproco de `case OrderType.RouteSegment` más abajo,
                // que resuelve `linkedOrderRoid` desde
                // `segmentLinkedBasicActionOrderIDs`/`segmentLinkedExternalOrderIDs`.
                // Aquí partimos de `basicActionLinkedSegmentOrderIDs` (en la
                // Order de la Actuació) → id de la Order del tram → su roid.
                // Zero queries extra si no hay vínculo.
                let linkedSegmentRoid: string | undefined;
                let linkedSegmentOrderId: string | undefined;
                let linkedSegmentEntityId: string | undefined;
                const linkedSegOrderId = order.basicActionLinkedSegmentOrderIDs?.[0];
                if (linkedSegOrderId) {
                  try {
                    const {
                      data: { order: linkedSegOrder },
                    } = await client.query({
                      query: GET_ORDER_BY_ID,
                      variables: { id: linkedSegOrderId },
                      // Calentado por el warm-up fase 2 (cross-links).
                      fetchPolicy: 'cache-first',
                    });
                    linkedSegmentRoid = linkedSegOrder?.roid;
                    linkedSegmentOrderId = linkedSegOrder?.id;
                    // Entity RouteSegment (trampa entidad doble: unlink/link
                    // quieren el id de la ENTITY, no el de la Order del tramo).
                    linkedSegmentEntityId = linkedSegOrder?.routeSegmentIDs?.[0];
                  } catch (err) {
                    console.log('Error fetching linked segment for action:', err);
                  }
                }

                actions.push({
                  id: order.id,
                  actionID: order.actionIDs[0], // ID de la Action para navegación al summary
                  roid: order.roid,
                  tag: order.state,
                  type: order.type,
                  date: order.registerInitDate,
                  endDate: order.registerEndDate,
                  creationDate: order.creationDate,
                  operators: users.map((user) => user.data.user).filter(Boolean),
                  // Origen (básica del RV = `surveillanceRoute`): la card lo
                  // usa para no pintar «Operarios» en una básica.
                  containerType: action.containerType,
                  laneCutting: action.laneCutting,
                  capitols: capitols,
                  capitol: order.capitolIDs[0],
                  subCapitol: order.subCapitolIDs?.[0],
                  subCapitolsValues: subCapitols,
                  predefine: order.predefinedOrderIDs?.[0] || action.predefinedOrderIDs?.[0],
                  predefinedOrders: predefinedOrders,
                  description: order.description,
                  notes: order.notes,
                  // El PK que pinta la tarjeta es el de la ENTIDAD, no el del
                  // padre ni el de su Order gemela: es lo que enseña el detalle,
                  // así que las dos pantallas no pueden discrepar. Medido contra
                  // beta (2026-09-14): de 120 hijas, 4 llevan el PK NULO en la
                  // Order y poblado en la entidad → leer la Order dejaría la
                  // línea en blanco.
                  pkInit: pkFormatter(action.pkInitKm, action.pkInitMeter),
                  pkEnd: pkFormatter(action.pkEndKm, action.pkEndMeter),
                  isTransferred: order.isTransferred,
                  state: order.state,
                  userName: childUserName,
                  registeredBy: childRegisteredBy,
                  imageIDs: order.imageIDs ?? [],
                  // P15 Fase 1 — tram vinculado (RV children únicamente).
                  linkedSegmentRoid,
                  linkedSegmentOrderId,
                  linkedSegmentEntityId,
                });
              }

              break;
            }
            case OrderType.Accident: {
              // T34: Fetch userName for the order creator
              let accidentUserName: string | undefined;
              if (order.userId) {
                try {
                  const {
                    data: { user },
                  } = await client.query({
                    query: GET_USER_BY_ID,
                    variables: {
                      input: {
                        id: order.userId,
                      },
                    },
                  });
                  accidentUserName = user ? `${user.name} ${user.surName}`.trim() : undefined;
                } catch (err) {
                  console.log('Error fetching user for accident order:', err);
                }
              }

              // Operario que registra (Order.registeredByUserId → "Nombre
              // Apellido"), mismo patrón que el creador. Si es null queda ''.
              let accidentRegisteredBy = '';
              if (order.registeredByUserId) {
                try {
                  const {
                    data: { user },
                  } = await client.query({
                    query: GET_USER_BY_ID,
                    variables: {
                      input: {
                        id: order.registeredByUserId,
                      },
                    },
                  });
                  accidentRegisteredBy = user ? `${user.name} ${user.surName}`.trim() : '';
                } catch (err) {
                  console.log('Error fetching registeredBy user for accident order:', err);
                }
              }

              // Mismo criterio que actuación y comunicación: el PK sale de la
              // ENTIDAD. Aquí hace falta pedirla, pero `warmupAccidentsByIds`
              // (arriba, mismo fragment NewAccident) ya la dejó en caché → el
              // cache-first da hit y no sale a red. Si el warm-up falló, miss
              // → red, que es exactamente lo que hace hoy la comunicación.
              let accidentPkInit = '';
              let accidentPkEnd = '';
              if (order.accidentIDs?.[0]) {
                try {
                  const {
                    data: { accident },
                  } = await client.query({
                    query: GET_ACCIDENT_BY_ID,
                    variables: { id: order.accidentIDs[0] },
                    fetchPolicy: 'cache-first',
                  });
                  accidentPkInit = pkFormatter(
                    accident.pkInitKm,
                    accident.pkInitMeter
                  );
                  accidentPkEnd = pkFormatter(
                    accident.pkEndKm,
                    accident.pkEndMeter
                  );
                } catch (err) {
                  console.log('Error fetching accident PK:', err);
                }
              }

              accidents.push({
                id: order.id,
                accidentID: order.accidentIDs[0], // ID del Accident para navegación al detail
                roid: order.roid,
                tag: order.state,
                type: order.type,
                date: order.registerInitDate,
                creationDate: order.creationDate,
                capitols: capitols,
                capitol: order.capitolIDs[0],
                subCapitol: order.subCapitolIDs[0],
                subCapitolsValues: subCapitols,
                predefine: order.predefinedOrderIDs?.[0],
                predefinedOrders: predefinedOrders,
                description: order.description,
                notes: order.notes,
                pkInit: accidentPkInit,
                pkEnd: accidentPkEnd,
                isTransferred: order.isTransferred,
                state: order.state,
                userName: accidentUserName,
                registeredBy: accidentRegisteredBy,
                imageIDs: order.imageIDs ?? [],
              });
              break;
            }
            case OrderType.Communication: {
              // T34: Fetch userName for the order creator
              let commUserName: string | undefined;
              if (order.userId) {
                try {
                  const {
                    data: { user },
                  } = await client.query({
                    query: GET_USER_BY_ID,
                    variables: {
                      input: {
                        id: order.userId,
                      },
                    },
                  });
                  commUserName = user ? `${user.name} ${user.surName}`.trim() : undefined;
                } catch (err) {
                  console.log('Error fetching user for communication order:', err);
                }
              }

              // T77: Fetch communication entity for sendStatus and interlocutor group name
              let commSendStatus: string | undefined;
              let commGroupInter: string | undefined;
              // Mismo criterio que la actuación: el PK sale de la ENTIDAD, que
              // es lo que lee el detalle. Aquí sale gratis — la entidad ya se
              // consulta justo debajo para el `sendStatus`.
              let commPkInit = '';
              let commPkEnd = '';
              if (order.communicationIDs?.[0]) {
                try {
                  const {
                    data: { communication },
                  } = await client.query({
                    query: GET_COMMUNICATION_BY_ID,
                    variables: {
                      id: order.communicationIDs[0],
                    },
                    // Frescura garantizada por el warm-up `communicationsByIds`
                    // network-only de arriba (mismo fragment NewCommunication,
                    // hit vía redirect Query.communication); si el warm-up
                    // falló, cache-first hace miss y va a red = hoy.
                    fetchPolicy: 'cache-first',
                  });
                  commSendStatus = communication.sendStatus;
                  commPkInit = pkFormatter(
                    communication.pkInitKm,
                    communication.pkInitMeter
                  );
                  commPkEnd = pkFormatter(
                    communication.pkEndKm,
                    communication.pkEndMeter
                  );

                  if (communication.interlocutorGroupIDs?.[0]) {
                    const {
                      data: { interlocutorGroup },
                    } = await client.query({
                      query: GET_INTERLOCUTOR_GROUP_BY_ID,
                      variables: {
                        id: communication.interlocutorGroupIDs[0],
                      },
                      // El catálogo `interlocutorGroups {id,name}` viaja en el
                      // boot (fetchDataBase) con el MISMO selection-set que
                      // esta query → redirect Query.interlocutorGroup de
                      // policies.ts + cache-first = 0 red; sin catálogo aún,
                      // miss → red = comportamiento de hoy.
                      fetchPolicy: 'cache-first',
                    });
                    commGroupInter = interlocutorGroup?.name;
                  }
                } catch (err) {
                  console.log('Error fetching communication details:', err);
                }
              }

              communications.push({
                id: order.id,
                communicationID: order.communicationIDs[0], // ID de la Communication para navegación al detail
                roid: order.roid,
                tag: order.state,
                type: order.type,
                date: order.registerInitDate,
                creationDate: order.creationDate,
                capitols: capitols,
                capitol: order.capitolIDs[0],
                subCapitol: order.subCapitolIDs[0],
                subCapitolsValues: subCapitols,
                predefine: order.predefinedOrderIDs?.[0],
                predefinedOrders: predefinedOrders,
                description: order.description,
                notes: order.notes,
                pkInit: commPkInit,
                pkEnd: commPkEnd,
                groupInter: commGroupInter,
                sendStatus: commSendStatus,
                isTransferred: order.isTransferred,
                state: order.state,
                userName: commUserName,
              });
              break;
            }
            case OrderType.RouteSegment: {
              // V4 P8 FR-8.3 (2026-04-22): renderiza el tramo bajo el RV padre.
              // Resuelve `linkedOrderRoid` desde `Order.segmentLinkedBasicActionOrderIDs`
              // (pauseCause='action') o `segmentLinkedExternalOrderIDs`
              // (pauseCause='external-call' + pendingExternalLink=false).
              const segmentId = order.routeSegmentIDs?.[0];
              if (!segmentId) break;

              let segmentEntity: {
                transfer?: {
                  fromRouteRoid?: string | null;
                  fromRoid?: string | null;
                  at: string;
                } | null;
                direction?: string;
                lane?: string;
                pkInitKm?: number;
                pkInitMeter?: number;
                pkEndKm?: number;
                pkEndMeter?: number;
                startDate?: string;
                pauseCause?: string | null;
                pauseCauseDetail?: string | null;
                pendingExternalLink?: boolean;
                pendingActionLink?: boolean;
                roadId?: string | null;
                roadStretches?: {
                  roadId: string;
                  pkInitKm: number;
                  pkInitMeter: number;
                  pkEndKm: number;
                  pkEndMeter: number;
                }[];
              } | null = null;
              try {
                const {
                  data: { routeSegment },
                } = await client.query({
                  query: GET_ROUTE_SEGMENT,
                  variables: { id: segmentId },
                  // Frescura garantizada por el warm-up `GetRouteSegments`
                  // plural network-only de arriba (MISMO fragment, hit vía
                  // redirect Query.routeSegment); si el warm-up falló,
                  // cache-first hace miss y va a red = comportamiento de hoy.
                  fetchPolicy: 'cache-first',
                });
                segmentEntity = routeSegment ?? null;
              } catch (err) {
                console.log('Error fetching route segment:', err);
              }

              // Sin entity no hay PKs/direction/lane/pauseCause válidos — skip la card
              // en vez de mostrar placeholders "0 + 000" confusos.
              if (!segmentEntity) {
                console.warn(
                  '[SR] RouteSegment entity not fetched, skipping card',
                  segmentId
                );
                break;
              }

              // Resolve linked order roid (cross-link Order↔Order).
              const basicLinkedOrderId =
                order.segmentLinkedBasicActionOrderIDs?.[0];
              let linkedOrderId =
                basicLinkedOrderId ?? order.segmentLinkedExternalOrderIDs?.[0];
              // Una básica ANULADA no resuelve el tramo (2026-10-08): la
              // tarjeta lo pinta pendiente y ofrece «Vincular a actuación» en
              // vez de «Resuelto por» una actuación muerta.
              let deadBasicLink = false;
              let linkedOrderRoid: string | undefined;
              // P15 Fase 1 (2026-07-08): id de la ENTITY Action vinculada (si
              // pauseCause=action) — target de navegación
              // `/work-orders/actions-validation/summary/:id`, distinto de
              // `linkedOrderId` (Order cruda, target de `/work-orders/:id`
              // cuando pauseCause=external-call). Ya viene en la misma
              // respuesta (`actionIDs` del fragment NewOrder) — cero queries extra.
              let linkedActionId: string | undefined;
              // P15 Fix ronda 1 (2026-07-08): el `type` de la Order externa
              // vinculada — una OT `external-call` puede ser `generic` O
              // `campaign` (el backend permite ambas, ver
              // route-segment.service.ts#linkExternalOrder). El consumidor
              // necesita distinguirlas para navegar al detail correcto
              // (`/work-orders/:id` vs `/work-orders/campaign/:id`), igual que
              // ActionOT.handleViewDetail. `type` ya viene en la misma
              // respuesta (fragment NewOrder) — cero queries extra.
              let linkedOrderType: OrderType | undefined;
              if (linkedOrderId) {
                try {
                  const {
                    data: { order: linkedOrder },
                  } = await client.query({
                    query: GET_ORDER_BY_ID,
                    variables: { id: linkedOrderId },
                    // Calentado por el warm-up fase 2 (cross-links).
                    fetchPolicy: 'cache-first',
                  });
                  if (
                    basicLinkedOrderId &&
                    linkedOrder &&
                    !isLiveLinkedBasicAction(linkedOrder)
                  ) {
                    deadBasicLink = true;
                    linkedOrderId = undefined;
                  } else {
                    linkedOrderRoid = linkedOrder?.roid;
                    linkedActionId = linkedOrder?.actionIDs?.[0];
                    linkedOrderType = linkedOrder?.type;
                  }
                } catch (err) {
                  console.log('Error fetching linked order for segment:', err);
                }
              }

              let segmentUserName: string | undefined;
              if (order.userId) {
                try {
                  const {
                    data: { user },
                  } = await client.query({
                    query: GET_USER_BY_ID,
                    variables: { input: { id: order.userId } },
                  });
                  segmentUserName = user
                    ? `${user.name} ${user.surName}`.trim()
                    : undefined;
                } catch (err) {
                  console.log('Error fetching user for segment order:', err);
                }
              }

              const segmentPkInit =
                segmentEntity.pkInitKm != null &&
                segmentEntity.pkInitMeter != null
                  ? pkFormatter(
                      segmentEntity.pkInitKm,
                      segmentEntity.pkInitMeter
                    )
                  : '—';
              const segmentPkEnd =
                segmentEntity.pkEndKm != null &&
                segmentEntity.pkEndMeter != null
                  ? pkFormatter(segmentEntity.pkEndKm, segmentEntity.pkEndMeter)
                  : '—';

              routeSegments.push({
                id: order.id,
                // D6 (2026-07-06): id de la ENTITY RouteSegment (entidad doble
                // Order↔RouteSegment). La mutation updateSegmentPauseCause y el
                // ?fromSegment del wizard esperan este id, NO el de la Order.
                segmentId,
                roid: order.roid,
                tag: order.state,
                type: order.type,
                date: order.registerInitDate,
                endDate: order.registerEndDate,
                startDate: segmentEntity.startDate
                  ? new Date(segmentEntity.startDate).getTime()
                  : undefined,
                pkInit: segmentPkInit,
                pkEnd: segmentPkEnd,
                // Por dónde fue el tramo. Va CRUDO: el nombre de cada vía lo
                // resuelve la tarjeta con el mismo hook que la cabecera del
                // recorrido, que es donde ya vive ese dato.
                //
                // 🔴 Un tramo que NO cruzó se normaliza a UN trozo con su
                // carretera (feedback usuario 2026-08-24). Antes se quedaba
                // sin lista y la tarjeta enseñaba dos PK sueltos sin decir de
                // qué vía eran — y en un recorrido de varias eso no se puede
                // deducir de la cabecera. Derivarlo aquí deja a la tarjeta con
                // una sola forma que pintar y con el MISMO formato de PK.
                //
                // Sin PK de cierre (tramo en curso) no se deriva nada: un
                // rango a medias es peor que las cajas de siempre.
                roadStretches: segmentEntity.roadStretches?.length
                  ? segmentEntity.roadStretches
                  : segmentEntity.roadId &&
                      segmentEntity.pkInitKm != null &&
                      segmentEntity.pkInitMeter != null &&
                      segmentEntity.pkEndKm != null &&
                      segmentEntity.pkEndMeter != null
                    ? [
                        {
                          roadId: segmentEntity.roadId,
                          pkInitKm: segmentEntity.pkInitKm,
                          pkInitMeter: segmentEntity.pkInitMeter,
                          pkEndKm: segmentEntity.pkEndKm,
                          pkEndMeter: segmentEntity.pkEndMeter,
                        },
                      ]
                    : undefined,
                direction: segmentEntity.direction,
                lane: segmentEntity.lane,
                description: order.description,
                pauseCause: segmentEntity.pauseCause,
                pauseCauseDetail: segmentEntity.pauseCauseDetail,
                pendingExternalLink: segmentEntity.pendingExternalLink,
                pendingActionLink:
                  segmentEntity.pendingActionLink || deadBasicLink,
                linkedOrderRoid,
                // P15 Fase 1 — targets de navegación de las pills vinculadas.
                linkedOrderId,
                linkedActionId,
                linkedOrderType,
                // Un tramo transferido no marca su Order (eso es de las
                // actuaciones): lo dice su rastro (revisión del 22-oct).
                isTransferred: order.isTransferred || !!segmentEntity.transfer,
                transferredFrom: segmentEntity.transfer ?? undefined,
                state: order.state,
                userName: segmentUserName,
                // Fotos de la intervención leve (2026-08-10). El fragment ya
                // las pedía, pero se perdían aquí: sin esta línea la card
                // recibía `undefined` y pintaba SIEMPRE el estado vacío, aunque
                // el tramo tuviera imágenes en la base de datos. Cazado en
                // navegador contra beta — ningún E2E lo veía porque sus mocks
                // nunca traían un tramo con fotos.
                imageIDs: order.imageIDs ?? [],
              });
              break;
            }
          }
        }
        } catch (err) {
          console.log('Error loading child order offline:', err);
        }
      })
    );

    if (
      actions.length > 0 ||
      accidents.length > 0 ||
      communications.length > 0 ||
      routeSegments.length > 0
    ) {
      // Spec 2026-09-23: cada actuación y accidente sabe de qué recorrido es,
      // con los `workOrderIds` que ya trajo la OT padre (orders[0]). Sin
      // consultas nuevas; si la padre pierde un recorrido, la hija también.
      const parent = orders[0] as
        | {
            linkedRoutes?: LinkedRouteRef[];
            unlinkedWorkRoutes?: UnlinkedWorkRouteRef[];
          }
        | undefined;
      const parentLinkedRoutes = parent?.linkedRoutes ?? [];
      // Spec § 16: y de qué patrulla SIN vincular es, para la etiqueta de aviso.
      const parentUnlinked = parent?.unlinkedWorkRoutes ?? [];
      const withWorkedInRoutes = <T extends { id: string }>(child: T) => ({
        ...child,
        workedInRoutes: routesForWork(parentLinkedRoutes, child.id),
        unlinkedRoutes: unlinkedRoutesForWork(parentUnlinked, child.id),
      });
      const joinedOrders = [
        ...actions.map(withWorkedInRoutes),
        ...accidents.map(withWorkedInRoutes),
        ...communications,
        ...routeSegments,
      ];
      joinedOrders.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      orders = [...orders, ...joinedOrders];
    }

    return orders;
  } catch (err) {
    Sentry.captureException(err);
    console.log('[órdenes] error inesperado', err);
    return [];
  }
};

// To fill tables
export const selectAllOrders = async (
  genericOrCampaignOrders: any[] = [],
  reFetch = false
) => {
  // Sentido, Tipo de vía y Carril se formatean aquí y quedan congelados en la
  // fila: sin las traducciones cargadas la celda enseñaría la clave en crudo
  // («surveillance.directionBoth (↕)») para siempre. Ver `utils/i18nReady.ts`.
  await ensureLabelNamespaces();

  log('🔧 Processing', genericOrCampaignOrders.length, 'orders');
  log('🔧 First order:', genericOrCampaignOrders[0]);

  // Pre-fetch all teams once to avoid N+1 queries
  let teamsMap: Record<string, string> = {};
  try {
    const { data: { teams } } = await client.query({ query: FETCH_TEAMS });
    teamsMap = Object.fromEntries((teams || []).map((t: any) => [t.id, t.name]));
  } catch {}

  // Resolver creador (Order.userId → "Nombre Apellido") leyendo FETCH_USERS
  // de la caché Apollo. Mismo patrón que selectAccidentOrders / selectActionOrders.
  // El loader de la página pre-carga FETCH_USERS antes de invocar este selector.
  const creatorMap: Record<string, string> = {};
  try {
    const usersData = client.readQuery({ query: FETCH_USERS });
    if (usersData?.users) {
      usersData.users.forEach(
        (u: { id: string; name?: string; surName?: string }) => {
          creatorMap[u.id] = `${u.name ?? ''} ${u.surName ?? ''}`.trim();
        },
      );
    }
  } catch {
    // Cache no disponible — columna Creador queda vacía como fallback
  }

  // Warm-up: UNA query `ordersByIds` puebla la caché con las Orders paralelas
  // de todas las filas; los GET_ORDER_BY_ID per-row de abajo pasan a
  // resolverse de caché (redirect Query.order). Si falla → no-op, per-row
  // intacto como fallback natural.
  await warmupOrdersByIds(
    genericOrCampaignOrders.map((o) => o?.orderIDs?.[0]),
    reFetch
  );

  const rows = await Promise.all(
    genericOrCampaignOrders.map(async (genericOrCampaignOrder, index) => {
      try {
        log(
          `🔧 Processing order ${index + 1}/${genericOrCampaignOrders.length}`,
          {
            orderIDs: genericOrCampaignOrder.orderIDs,
            id: genericOrCampaignOrder.id,
          }
        );

        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: genericOrCampaignOrder.orderIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        });

        // Dato inconsistente en BD: la entidad doble (GenericOrder/Campaign)
        // referencia un Order que ya no existe (dangling; p.ej. un borrado que
        // no arrastró ambas mitades). NO se oculta en silencio: se REPORTA a
        // Sentry + consola (para que se detecte y arregle) y se omite la fila
        // para no tumbar toda la tabla. Tolerar-pero-cantar, no esconder.
        if (!order) {
          const msg = `[selectAllOrders] Entidad doble huérfana: ${genericOrCampaignOrder.id} → orderIDs[0]=${genericOrCampaignOrder.orderIDs?.[0]} no existe como Order. Fila omitida.`;
          console.warn(msg);
          Sentry.captureMessage(msg, 'warning');
          return null;
        }

        log(`✅ Order ${index + 1} fetched:`, {
          orderId: order.id,
          roid: order.roid,
          state: order.state,
        });

        const [
          {
            data: { capitol },
          },
          {
            data: { subCapitol },
          },
          {
            data: { classification },
          },
          {
            data: { road },
          },
          {
            data: { concession },
          },
        ] = await Promise.all([
          client.query({
            query: GET_CAPITOL_BY_ID,
            variables: {
              id: order.capitolIDs[0],
            },
          }),
          client.query({
            query: GET_SUB_CAPITOL_BY_ID,
            variables: {
              id: order.subCapitolIDs[0],
            },
          }),
          client.query({
            query: GET_CLASSIFICATION_BY_ID_OR_ROID,
            variables: {
              id: order.classificationIDs[0],
            },
          }),
          client.query({
            query: GET_ROAD_BY_ID,
            variables: {
              id: genericOrCampaignOrder.roadIDs[0],
            },
          }),
          client.query({
            query: GET_CONCESSION_BY_ID,
            variables: {
              id: genericOrCampaignOrder.concessionIDs[0],
            },
          }),
        ]);
        let predefinedOrderName = '';
        const predefinedId = order.predefinedOrderIDs?.[0];
        if (predefinedId) {
          try {
            const cached = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
            predefinedOrderName = cached?.predefinedOrders?.find((p: any) => p.id === predefinedId)?.name ?? '';
          } catch { /* cache not available */ }
        }

        // Un genericOrder/campaign puede tener 1 o varios equipos: mostrarlos
        // todos (no solo el primero). teamsMap mapea teamId -> name.
        const teamName = (genericOrCampaignOrder?.teamIDs ?? [])
          .map((teamId: string) => teamsMap[teamId])
          .filter(Boolean)
          .join(', ');

        const result = {
          id: order.id,
          name: genericOrCampaignOrder?.name,
          roid:
            typeof order.roid === 'string'
              ? order.roid.split('.')
              : Array.isArray(order.roid)
                ? order.roid
                : [],
          creationDate: order.creationDate,
          registerInitDate: order.registerInitDate,
          derivedEndDate: order.derivedEndDate ?? null,
          capitol: capitol.name,
          subCapitol: subCapitol.name,
          classification: classification.name,
          concession: concession.name,
          road: road.name,
          pkInit: pkFormatter(
            genericOrCampaignOrder.pkInitKm,
            genericOrCampaignOrder.pkInitMeter
          ),
          pkEnd: pkFormatter(
            genericOrCampaignOrder.pkEndKm,
            genericOrCampaignOrder.pkEndMeter
          ),
          state: order.state,
          description: order.description,
          notes: order.notes,
          // Display unificado de "Sentido" (helper compartido) — cubre esta
          // tabla + históricos + campañas + modales relacionar/transferir que
          // reusan `selectAllOrders`. Formato "Texto (símbolo)".
          direction: formatDirectionCellLabel(
            genericOrCampaignOrder?.direction,
            road?.directions
          ),
          track: trackLabel(genericOrCampaignOrder?.track),
          genericOrCampaignOrderID: genericOrCampaignOrder.id,
          predefinedOrder: predefinedOrderName,
          team: teamName,
          user: order.userId ? (creatorMap[order.userId] ?? '') : '',
          // Sin `registeredBy`: este selector alimenta órdenes genéricas y
          // campañas, y "Registrado por" solo se rellena al crear Actuación o
          // Accidente. Su único consumidor era la columna del Excel de
          // históricos, que salía siempre vacía.
          // T36: IDs for pre-fill in CreateOT when selecting from modal
          capitolId: order.capitolIDs[0],
          subCapitolId: order.subCapitolIDs[0],
          classificationId: order.classificationIDs[0],
          predefinedOrderId: order.predefinedOrderIDs?.[0],
        };

        return result;
      } catch (error) {
        // Resiliencia por-fila: un fallo al enriquecer UNA orden (Order
        // huérfana, catálogo ausente, etc.) NO debe tumbar toda la tabla.
        // Se omite esa fila (se registra en Sentry) y el resto se pinta.
        Sentry.captureException(error);
        console.warn(
          '[selectAllOrders] fila omitida por error de enriquecido:',
          error
        );
        return null;
      }
    })
  );
  // Type-guard (no `filter(Boolean)`): estrecha (T | null)[] → T[] para que los
  // consumidores (setGenericOrdersData/setCampaignData) reciban dataTable[] limpio.
  return rows.filter((row): row is NonNullable<typeof row> => row !== null);
};

// Summary data
export const selectWorkOrderById = async (
  orderId: string,
  isCampaign = false
) => {
  let dataFromOrderDependsOnType: {
    concessionIDs: string[];
    roadIDs: string[];
    teamIDs?: string[];
    pkInit: number;
    pkInitKm: number;
    pkInitMeter: number;
    pkEnd: number;
    pkEndKm: number;
    pkEndMeter: number;
    track: string;
    direction: string;
    margin: string;
    lane: string;
    name: string;
  };

  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: orderId,
    },
  });

  if (isCampaign) {
    const {
      data: { campaign },
    } = await client.query({
      query: GET_CAMPAIGN_BY_ID,
      variables: {
        id: order.campaignIDs[0],
      },
    });
    dataFromOrderDependsOnType = campaign;
  } else {
    const {
      data: { genericOrder },
    } = await client.query({
      query: GET_GENERIC_ORDER_BY_ID,
      variables: {
        id: order.genericOrdersIDs[0],
      },
    });

    dataFromOrderDependsOnType = genericOrder;
  }

  let teamName = '';
  if (dataFromOrderDependsOnType?.teamIDs?.[0]) {
    try {
      const { data: { team } } = await client.query({
        query: GET_TEAM_BY_ID,
        variables: { id: dataFromOrderDependsOnType.teamIDs[0] },
      });
      teamName = team?.name || '';
    } catch {}
  }

  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: dataFromOrderDependsOnType.concessionIDs[0],
      },
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: dataFromOrderDependsOnType.roadIDs[0],
      },
    }),
  ]);

  const [
    {
      data: { capitol },
    },
    {
      data: { subCapitol },
    },
    {
      data: { classification },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CAPITOL_BY_ID,
      variables: {
        id: order.capitolIDs[0],
      },
    }),
    client.query({
      query: GET_SUB_CAPITOL_BY_ID,
      variables: {
        id: order.subCapitolIDs[0],
      },
    }),
    client.query({
      query: GET_CLASSIFICATION_BY_ID_OR_ROID,
      variables: {
        id: order.classificationIDs[0],
      },
    }),
  ]);

  return {
    id: order.id,
    roid: order.roid.split('.'),
    type: order.type,
    registerInitDate: order.registerInitDate,
    concession: concession.name,
    road: road.name,
    pkInit: pkFormatter(
      dataFromOrderDependsOnType.pkInitKm,
      dataFromOrderDependsOnType.pkInitMeter
    ),
    pkEnd: pkFormatter(
      dataFromOrderDependsOnType.pkEndKm,
      dataFromOrderDependsOnType.pkEndMeter
    ),
    description: order.description,
    notes: order.notes,
    capitol: capitol.name,
    subCapitol: subCapitol.name,
    classification: classification.name,
    track: trackLabel(dataFromOrderDependsOnType.track),
    roadDirections: road.directions,
    direction: dataFromOrderDependsOnType?.direction,
    margin: dataFromOrderDependsOnType?.margin,
    lane: laneLabel(dataFromOrderDependsOnType.lane),
    team: teamName,
    city: concession.location,
    pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter, true),
    pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter, true),
    campaign: isCampaign && dataFromOrderDependsOnType.name,
    userId: order.userId,
  };
};
